import GeoJSON from 'ol/format/GeoJSON';
import WFS from 'ol/format/WFS';
import { bbox as bboxStrategy } from 'ol/loadingstrategy';
import VectorSource, { type LoadingStrategy } from 'ol/source/Vector';
import { type IMapLayerWfsOptions } from '../MapLayer.ts';
import {
  buildWfsGetFeatureUrl,
  WfsDefaultSrsName,
  WfsDefaultVersion,
} from './buildWfsGetFeatureUrl.ts';
import { registerItalianProjections } from './registerItalianProjections.ts';

export interface IWfsVectorSourceOptions {
  /** Strategia di caricamento; default: bbox */
  strategy?: LoadingStrategy;
}

/**
 * Formato di parsing delle risposte GetFeature: GeoJSON quando il
 * servizio lo dichiara nell'`outputFormat`, altrimenti GML tramite
 * `ol/format/WFS` (GML 3.1.1 per WFS 1.1.0, GML 3.2.1 per WFS 2.0.0,
 * selezionato in base alla versione).
 *
 * Le feature sono richieste con `srsName=EPSG:3857` ma il GeoJSON
 * RFC 7946 non trasporta il CRS: `dataProjection` allinea il default
 * di lettura al CRS richiesto. Un eventuale membro `crs` nella
 * risposta ha comunque la precedenza.
 */
export function createWfsFeatureFormat(
  wfs: IMapLayerWfsOptions,
): GeoJSON | WFS {
  if (wfs.outputFormat?.toLowerCase().includes('json')) {
    return new GeoJSON({ dataProjection: WfsDefaultSrsName });
  }
  return new WFS({ version: wfs.version ?? WfsDefaultVersion });
}

/**
 * VectorSource per un layer WFS con caricamento per bounding box.
 * Le feature sono richieste in EPSG:3857 (`srsName`); se il servizio
 * risponde comunque in un altro CRS, OpenLayers riproietta client-side
 * grazie ai CRS italiani registrati.
 */
export function createWfsVectorSource(
  rawUrl: string,
  wfs: IMapLayerWfsOptions,
  options: IWfsVectorSourceOptions = {},
): VectorSource {
  registerItalianProjections();

  return new VectorSource({
    format: createWfsFeatureFormat(wfs),
    url: extent => buildWfsGetFeatureUrl({ baseUrl: rawUrl, wfs, extent }),
    strategy: options.strategy ?? bboxStrategy,
  });
}
